import type { AdminAuditActorType } from '@/api/models';

/** The seven actor types the catalog defines (`audit-events.md`), fixed — used to drive the actor-type filter. */
export const AUDIT_ACTOR_TYPES: AdminAuditActorType[] = [
  'platform_admin',
  'platform_auditor',
  'platform_staff',
  'application',
  'system_process',
  'gateway',
  'guest',
];

/**
 * The closed catalog of 43 action codes (`audit-events.md`), used to drive
 * the action filter's options. Keep in sync with that document — it is the
 * single normative source, this is a consumer-side mirror, same discipline
 * as `src/api/models.ts` against the OpenAPI contract.
 */
export const AUDIT_ACTION_CODES: string[] = [
  'credential.created',
  'credential.rotated',
  'credential.revoked',
  'credential.allowed_tenants_changed',
  'credential.ip_allowlist_changed',
  'gateway_account.created',
  'gateway_account.updated',
  'gateway_account.status_changed',
  'gateway_secret.written',
  'gateway_secret.rotated',
  'gateway_secret.retired',
  'vault.dek_unwrapped',
  'vault.credential_decrypted',
  'tenant.created',
  'tenant.status_changed',
  'tenant.tag_changed',
  'tenant_policy.changed',
  'payment_intent.created',
  'payment_intent.status_changed',
  'payment_intent.transition_rejected',
  'payment_operation.recorded',
  'checkout_session.consumed',
  'checkout_session.access_denied',
  'gateway_event.received',
  'gateway_event.signature_invalid',
  'gateway_event.duplicate_discarded',
  'notification.enqueued',
  'notification.attempted',
  'notification.exhausted',
  'notification.resent_manually',
  'report.queried',
  'report.exported',
  'report.query_rejected',
  'auth.login_succeeded',
  'auth.login_failed',
  'auth.access_denied',
  'auth.credential_rejected',
  'audit_log.queried',
  'audit_log.exported',
  'audit.recording_paused',
  'audit.recording_resumed',
  'test_bench.preset_linked',
  'test_bench.payment_launched',
];

/**
 * The safety-critical piece of this screen: for a given action code, the
 * *only* `metadata` keys the entry detail may ever render, taken verbatim
 * from the "Required metadata keys" column of `audit-events.md`. The detail
 * view iterates this list — never `Object.keys(entry.metadata)` — so a key
 * the API might unexpectedly attach to a row (a bug, a future field added to
 * the catalog before this mirror is updated) is silently not displayed
 * rather than dumped to the screen. An action code absent from this map
 * (should not happen against a contract-conformant API) renders zero
 * metadata rows, never a raw fallback dump.
 */
export const AUDIT_ACTION_METADATA_KEYS: Record<string, string[]> = {
  'credential.created': ['application_id', 'key_id', 'allowed_tenants', 'ip_allowlist_set'],
  'credential.rotated': ['key_id', 'previous_key_id'],
  'credential.revoked': ['key_id', 'reason'],
  'credential.allowed_tenants_changed': ['key_id', 'added', 'removed'],
  'credential.ip_allowlist_changed': ['key_id', 'added', 'removed'],
  'gateway_account.created': ['tenant_id', 'gateway', 'mode', 'alias'],
  'gateway_account.updated': ['gateway_account_id', 'changed_fields'],
  'gateway_account.status_changed': ['gateway_account_id', 'previous_status', 'new_status'],
  'gateway_secret.written': ['gateway_account_id', 'field_name', 'version', 'fingerprint'],
  'gateway_secret.rotated': ['gateway_account_id', 'field_name', 'previous_version', 'new_version', 'fingerprint'],
  'gateway_secret.retired': ['gateway_account_id', 'field_name', 'version'],
  'vault.dek_unwrapped': ['gateway_account_id', 'key_management_ref', 'reason'],
  'vault.credential_decrypted': ['gateway_account_id', 'field_name', 'version', 'payment_intent_id', 'process_id'],
  'tenant.created': ['tenant_code', 'name'],
  'tenant.status_changed': ['tenant_id', 'previous_status', 'new_status'],
  'tenant.tag_changed': ['tenant_id', 'key', 'previous_value', 'new_value'],
  'tenant_policy.changed': ['tenant_id', 'changed_fields'],
  'payment_intent.created': ['intent_id', 'tenant_id', 'application_id', 'type', 'amount', 'currency', 'order_reference'],
  'payment_intent.status_changed': ['intent_id', 'previous_status', 'new_status', 'trigger'],
  'payment_intent.transition_rejected': ['intent_id', 'attempted_status', 'current_status', 'reason'],
  'payment_operation.recorded': ['intent_id', 'operation_id', 'kind', 'amount', 'status'],
  'checkout_session.consumed': ['intent_id', 'consumed_ip', 'gateway'],
  'checkout_session.access_denied': ['token_fingerprint', 'reason'],
  'gateway_event.received': ['gateway', 'external_event_id', 'signature_ok', 'size_bytes'],
  'gateway_event.signature_invalid': ['gateway', 'external_event_id', 'reason'],
  'gateway_event.duplicate_discarded': ['gateway', 'external_event_id'],
  'notification.enqueued': ['intent_id', 'notification_id'],
  'notification.attempted': ['intent_id', 'notification_id', 'attempt', 'url_host', 'status_code'],
  'notification.exhausted': ['intent_id', 'notification_id', 'attempts'],
  'notification.resent_manually': ['intent_id', 'notification_id', 'actor_role'],
  'report.queried': ['endpoint', 'tenant_codes_requested', 'filters_hash', 'result_count'],
  'report.exported': ['endpoint', 'tenant_codes_requested', 'filters_hash', 'row_count', 'format'],
  'report.query_rejected': ['endpoint', 'tenant_codes_requested', 'unauthorized_tenant_codes'],
  'auth.login_succeeded': ['user_id', 'role', 'mfa_used', 'ip'],
  'auth.login_failed': ['attempted_user', 'ip', 'reason'],
  'auth.access_denied': ['attempted_object_type', 'attempted_object_id', 'required_role_or_scope'],
  'auth.credential_rejected': ['key_id_prefix', 'reason'],
  'audit_log.queried': ['filters_hash', 'result_count'],
  'audit_log.exported': ['filters_hash', 'row_count'],
  'audit.recording_paused': ['component', 'reason'],
  'audit.recording_resumed': ['component'],
  'test_bench.preset_linked': ['tenant_id', 'gateway_account_id', 'preset_code'],
  'test_bench.payment_launched': ['tenant_id', 'gateway_account_id', 'amount', 'intent_id'],
};

/** Metadata keys whose string value is a serialized boolean (`"true"`/`"false"`), for a friendlier Sí/No rendering. */
const BOOLEAN_METADATA_KEYS = new Set(['signature_ok', 'mfa_used', 'ip_allowlist_set']);

export function isBooleanMetadataKey(key: string): boolean {
  return BOOLEAN_METADATA_KEYS.has(key);
}

/** Metadata keys that carry an integer amount in minor units (never a decimal — format only here, at the presentation layer). */
const AMOUNT_METADATA_KEYS = new Set(['amount']);

export function isAmountMetadataKey(key: string): boolean {
  return AMOUNT_METADATA_KEYS.has(key);
}

/**
 * Ordered list of `(key, value)` pairs to render for one entry's metadata
 * panel: only the keys `AUDIT_ACTION_METADATA_KEYS` whitelists for this
 * action, in the catalog's own order, and only when the key is actually
 * present on the entry — never every key the object happens to carry.
 */
export function whitelistedMetadataEntries(action: string, metadata: Record<string, string>): Array<[string, string]> {
  const allowedKeys = AUDIT_ACTION_METADATA_KEYS[action] ?? [];
  const entries: Array<[string, string]> = [];
  for (const key of allowedKeys) {
    const value = metadata[key];
    if (value !== undefined) entries.push([key, value]);
  }
  return entries;
}
